import {
  Button,
  CircularProgress,
  TextField,
  makeStyles,
  withStyles,
} from "@material-ui/core";
import { ReactComponent as DetailsIcon } from "../images/details.svg";
import { useParams, useHistory } from "react-router-dom";
import { useAsync, useAsyncFn } from "react-use";
import axios from "axios";
import { useEffect, useState } from "react";
import { ArrowBack } from "@material-ui/icons";

const useStyles = makeStyles(() => ({
  loading: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    flexDirection: "column",

    "& p": {
      marginTop: 16,
    },
  },
  stepLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    "& .trade-in-status": {
      "& .status": {
        fontSize: 14,
      },
      "& .date": {
        fontSize: 14,
        fontWeight: 600,
        color: "var(--gray-9)",
      },
    },
  },
  activeStep: {
    "& svg": {
      color: "var(--green-2) !important",
    },
  },
  close: {
    overflow: "hidden",
    maxHeight: 0,
    padding: "0px !important",
    borderBottomRightRadius: "4px !important",
    borderBottomLeftRadius: "4px !important",
  },
  open: {
    maxHeight: 1000,
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    "& .modal-title": {
      margin: 0,
    },
  },
  shadow: {
    boxShadow: "0px 2px 4px 0px rgba(33, 37, 41, 0.08)",
  },
  mainPadding: {
    paddingInline: 80,
    paddingBlock: 40,

    "@media (max-width: 768px)": {
      padding: "20px 0px",
    },
  },
  detailsHeader: {
    borderRadius: "4px 4px 0px 0px",
    border: "1px solid var(--gray-3)",
    borderBottom: "1px solid var(--gray-2)",
    background: "var(--gray-1)",
    padding: 16,
    height: 72,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    "& .content": {
      display: "flex",
      alignItems: "center",

      "& .texts": {
        marginLeft: 12,
        "& .t1": {
          margin: 0,
          fontSize: 13,
          color: "var(--gray-7)",
          lineHeight: "20px",
        },
        "& .t2": {
          margin: 0,
          fontSize: 16,
          fontWeight: 600,
          color: "var(--gray-9)",
          lineHeight: "24px",
        },
        "& .semibold": {
          fontWeight: 600,
        },

        "& .darker": {
          color: "var(--gray-9)",
        },
      },
    },
  },
  detailsContent: {
    borderBottomRightRadius: 4,
    borderBottomLeftRadius: 4,
    border: "1px solid var(--gray-3)",
    borderTop: "none",
    padding: 20,

    "& .row": {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridGap: 26,
      gridRowGap: 36,

      "@media (max-width: 768px)": {
        gridTemplateColumns: "1fr",
        gridRowGap: 20,
      },

      "& .col": {
        "& .title": {
          margin: 0,
          fontSize: 13,
          color: "var(--gray-7)",
          lineHeight: "20px",
        },
        "& .value": {
          margin: 0,
          fontSize: 16,
          fontWeight: 600,
          color: "var(--gray-9)",
          lineHeight: "24px",
        },
      },
    },
  },
  divider: {
    height: 1,
    background: "var(--gray-2)",
    marginBlock: 24,
  },
  sectionTitle: {
    margin: "0px 0px 16px",
    fontSize: 16,
    fontWeight: 600,
    color: "var(--gray-9)",
    lineHeight: "24px",

    "&.marginTop": {
      marginTop: 24,
    },
  },
  viewImagesBtn: {
    fontSize: 13,
    fontWeight: 600,
    color: "#5046B2",
    lineHeight: "18px",
    background: "none",
    border: "none",
    cursor: "pointer",
    marginTop: 20,

    "& span": {
      marginLeft: 6,
    },
  },
  successText: {
    color: "var(--green-2)",
    fontSize: 14,
    fontWeight: 500,
  },
  customerInfoHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  editInfo: {
    fontWeight: 600,
  },
  saveChangesContainer: {
    marginTop: 32,
    textAlign: "right",
    "@media (max-width: 768px)": {
      "& button": {
        width: "100%",
      },
    },
  },
}));

const ColorButton = withStyles((theme) => ({
  root: {
    color: "white",
    background: "#5046B2",
    "&:hover": {
      background: "#413992",
    },
    fontSize: 14,
    fontWeight: 600,
    textTransform: "capitalize",
    fontFamily: "Inter",
  },
}))(Button);

const TextButton = withStyles((theme) => ({
  root: {
    color: "#5046B2",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "capitalize",
    marginBottom: 25,
  },
}))(Button);

const FullWidthInput = withStyles((theme) => ({
  root: {
    width: "100%",
  },
}))(TextField);

const CustomerInformation = () => {
  const history = useHistory();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    country: "",
    zip: "",
    email: "",
    phone: "",
  });
  const classes = useStyles();
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { tradeinid } = useParams();
  const { loading, value: tradeInDetails } = useAsync(async () => {
    const quote = await axios.post(
      "/tradeindetails/getDetails",
      {
        filter: tradeinid,
      },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("userId")}`,
        },
      }
    );
    return await quote.data.data?.[0];
  });

  const [updateCustomerInfoState, postUpdateCustomerInfo] =
    useAsyncFn(async () => {
      // Timeout mechanism (set timeout for API call)
      const timeout = setTimeout(() => {
        setError("Save operation timed out. Please try again.");
        setIsSubmitting(false);
      }, 10000); // 10 seconds timeout

      try {
        setIsSubmitting(true); // Start loading
        setError(null); // Reset previous errors

        const response = await axios.post(
          "/tradeindetails/updateAddress",
          { tradeinId: tradeInDetails.id, address: form },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("userId")}`,
            },
          }
        );

        clearTimeout(timeout); // Clear timeout if API call succeeds
        setIsSubmitting(false); // Stop loading after the operation completes

        return response.data;
      } catch (err) {
        clearTimeout(timeout); // Clear timeout if there is an error
        setIsSubmitting(false); // Stop loading

        // Handle error appropriately
        setError("An error occurred while saving. Please try again.");
        console.error("Error during save operation:", err);
        throw err; // Re-throw error for asyncFn handling
      }
    }, [form, tradeInDetails]);

  useEffect(() => {
    return () => {
      // Cleanup: Abort any ongoing requests if the component is unmounted
      setIsSubmitting(false);
      setError(null);
    };
  }, []);

  useEffect(() => {
    if (tradeInDetails) {
      const {
        ctiQuoteParent: {
          firstName,
          lastName,
          address1,
          address2,
          city,
          state,
          country,
          zip,
          email,
          phone,
        },
      } = tradeInDetails;
      setForm({
        firstName,
        lastName,
        address1,
        address2,
        city,
        state,
        country,
        zip,
        email,
        phone,
      });
    }
  }, [tradeInDetails]);

  if (loading)
    return (
      <div className={classes.loading}>
        <CircularProgress />
        <p>Loading Details</p>
      </div>
    );

  const onChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <>
      <div className={classes.mainPadding}>
        <TextButton
          onClick={() => history.goBack()}
          startIcon={<ArrowBack />}
          variant="text"
        >
          Back to previous page
        </TextButton>
        {error && <div style={{ color: "#ff0000" }}>{error}</div>}
        <div className={classes.shadow}>
          <div className={classes.detailsHeader}>
            <div className="content">
              <DetailsIcon />
              <div className="texts">
                <p className="t2">Customer Information</p>
              </div>
            </div>
          </div>
          <div className={classes.detailsContent}>
            <div className={classes.customerInfoHeader}>
              <h2 className={classes.sectionTitle}>Personal Information</h2>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                postUpdateCustomerInfo().then(() => {
                  history.push(`/TradeInDetails/${tradeinid}`);
                });
              }}
            >
              <div className="row">
                <div className="col">
                  <FullWidthInput
                    value={form.firstName}
                    onChange={(e) => onChange("firstName", e.target.value)}
                    required
                    size="small"
                    label="First Name"
                    variant="outlined"
                  />
                </div>
                <div className="col">
                  <FullWidthInput
                    value={form.lastName}
                    onChange={(e) => onChange("lastName", e.target.value)}
                    required
                    size="small"
                    label="Last Name"
                    variant="outlined"
                  />
                </div>
              </div>
              <div className={classes.divider}></div>
              <h2 className={classes.sectionTitle}>Contact Information</h2>
              <div className="row">
                <div className="col">
                  <FullWidthInput
                    defaultValue=" "
                    value={form.email}
                    onChange={(e) => onChange("email", e.target.value)}
                    disabled
                    size="small"
                    label="Email Address"
                    variant="outlined"
                  />
                </div>
                <div className="col">
                  <FullWidthInput
                    defaultValue=" "
                    value={form.phone}
                    onChange={(e) => onChange("phone", e.target.value)}
                    disabled
                    size="small"
                    label="Contact Number"
                    variant="outlined"
                  />
                </div>
                <div className="col">
                  <FullWidthInput
                    value={form.address1}
                    onChange={(e) => onChange("address1", e.target.value)}
                    required
                    size="small"
                    label="Street Address"
                    variant="outlined"
                  />
                </div>
                <div className="col">
                  <FullWidthInput
                    value={form.address2}
                    onChange={(e) => onChange("address2", e.target.value)}
                    size="small"
                    label="Apartment, Suit or Building"
                    variant="outlined"
                  />
                </div>
                <div className="col">
                  <FullWidthInput
                    value={form.city}
                    onChange={(e) => onChange("city", e.target.value)}
                    required
                    size="small"
                    label="City"
                    variant="outlined"
                  />
                </div>
                <div className="col">
                  <FullWidthInput
                    value={form.state}
                    onChange={(e) => onChange("state", e.target.value)}
                    size="small"
                    label="Province"
                    variant="outlined"
                  />
                </div>
                <div className="col">
                  <FullWidthInput
                    value={form.zip}
                    onChange={(e) => onChange("zip", e.target.value)}
                    required
                    size="small"
                    label="Postal Code"
                    variant="outlined"
                  />
                </div>
              </div>
              <div className={classes.saveChangesContainer}>
                <ColorButton
                  disabled={updateCustomerInfoState.loading}
                  type="submit"
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </ColorButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomerInformation;
